import DataManager from 'utils/DataManager/dataManager';
import { IEligibilityRule } from 'typings/eligibilityRule';
import { IProrationRule } from 'typings/prorationRule';
import { IConfiguration } from 'typings/configuration';
import { ICycleConfiguration } from 'typings/cycleConfiguration';
import { IBaseEmployeeLevel } from 'typings/baseEmployeeLevel';
import { IWorkflowConfiguration } from 'typings/workflowConfiguration';
import { StockDisplayType, ProrateMethod } from 'typings/enums';
import { ICycleBudget } from 'typings/cycleBudget';
import { IObservableArray } from 'mobx';
import { ICycleBudgetField } from 'typings/cycleBudgetField';
import { RuleCode } from 'typings/enums';

interface IWorkflowConfigurationRequest {
    workflowConfigurationValue: string;
}

interface IComposedCycleConfiguration {
    cycleId: number;
    startDate: string;
    endDate: string;
    payEffectiveDate: string;
    value: string;
    workflowConfigurationValue?: string;
    previewMode: boolean;
}

export interface ICycleConfigurationFromService {
    value: string;
    id: number;
    cycleId: number;
    startDate: string;
    endDate: string;
    payEffectiveDate: string;
    closedDate: string;
    budgetFields: ICycleBudgetFieldFromService[];
    budgets: IObservableArray<ICycleBudget>;
    workflowConfigurationValue: string;
    status: string;
    previewMode: boolean;
}

interface ICycleBudgetFieldValue {
    proration: {
        autoProrate: boolean;
        prorateMethod: ProrateMethod;
        prorateStartDate: string;
        prorateEndDate: string;
        calendarDuration: number;
        numberOfDaysInAMonth: number;
    };
    percentageCalculationRule?: string;
    rangeEnforcement?: IRangeEnforcement;
    stock?: {
        displayType: StockDisplayType;
        value: number;
    };
}

interface ICycleBudgetFieldFromService extends ICycleBudgetField {
    value: string;
}

interface IEligibilityRuleFromService extends IEligibilityRule {
    amountCycleIds: number[];
}

interface IProrationRuleFromService extends IProrationRule {
    amountCycleIds: number[];
}

export interface IConfigurationFromService extends IConfiguration {
    value: string;
}

class ConfigurationDataManager extends DataManager {
    private readonly defaultCsvFilename: string = 'Workflow-Export.csv';

    async addNewEligibilityRule(
        masterAccountId: string,
        cycleId: number,
        eligibilityRule: IEligibilityRule,
        amountCycleIds: number[]): Promise<Nullable<number>> {
        const eligibilityRuleFromService = {
            comment: eligibilityRule.comment,
            amountCycleIds
        } as IEligibilityRuleFromService;

        const receive = () =>
            this._postDataRequest<IEligibilityRuleFromService, number>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/eligibilityrules`,
                eligibilityRuleFromService);
        return await this.getData<number>(receive, false);
    }

    async updateEligibilityRule(
        masterAccountId: string,
        cycleId: number,
        eligibilityRule: IEligibilityRule,
        amountCycleIds: number[]): Promise<Nullable<number>> {
        const eligibilityRuleFromService = {
            eligibilityRuleId: eligibilityRule.eligibilityRuleId,
            comment: eligibilityRule.comment,
            amountCycleIds
        } as IEligibilityRuleFromService;

        const receive = () =>
            this._putDataRequest<IEligibilityRuleFromService, number>(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/eligibilityrules/${eligibilityRule.eligibilityRuleId}`,
                eligibilityRuleFromService);
        return await this.getData<number>(receive, false);
    }

    async updateProrationRule(
        masterAccountId: string,
        cycleId: number,
        prorationRule: IProrationRule,
        amountCycleIds: number[]): Promise<Nullable<number>> {
        const prorationRuleFromService = {
            prorationRuleId: prorationRule.prorationRuleId,
            prorateMethod: prorationRule.prorateMethod,
            ruleFilter: prorationRule.ruleFilter,
            calendarDuration: prorationRule.calendarDuration,
            numberOfDaysInAMonth: prorationRule.numberOfDaysInAMonth,
            amountCycleIds
        } as IProrationRuleFromService;

        const receive = () =>
            this._putDataRequest<IProrationRuleFromService, number>(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/prorationrules/${prorationRule.prorationRuleId}`,
                prorationRuleFromService);
        return await this.getData<number>(receive, false);
    }

    async addNewProrationRule(
        masterAccountId: string,
        cycleId: number,
        prorationRule: IProrationRule,
        amountCycleIds: number[]): Promise<Nullable<number>> {
        const prorationRuleFromService = {
            prorateMethod: prorationRule.prorateMethod,
            ruleFilter: prorationRule.ruleFilter,
            calendarDuration: prorationRule.calendarDuration,
            numberOfDaysInAMonth: prorationRule.numberOfDaysInAMonth,
            amountCycleIds
        } as IProrationRuleFromService;

        const receive = () =>
            this._postDataRequest<IProrationRuleFromService, number>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/prorationrules`,
                prorationRuleFromService);
        return await this.getData<number>(receive, false);
    }

    async getProrationRules(masterAccountId: string, cycleId: number): Promise<Nullable<IProrationRule[]>> {
        const receive = () => this._getDataRequest<IProrationRule[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/prorationrules`);
        return await this.getData<IProrationRule[]>(receive, false);
    }

    async deleteProrationRule(
        masterAccountId: string,
        cycleId: number,
        prorationRuleId: number): Promise<Nullable<number>> {
        const receive = () => this._deleteDataRequest<number>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/prorationrules/${prorationRuleId}`);
        return await this.getData<number>(receive, false);
    }

    async getConfiguration(masterAccountId: string): Promise<IConfiguration> {
        const receive = () => this._getDataRequest<IConfigurationFromService>(
            `/api/accounts/${masterAccountId}/team/configuration`);
        const result = await this.getData<IConfigurationFromService>(receive, false);
        if (!result) {
            throw Error('Unable to retrieve configuration');
        }

        const configuration = this.parseConfiguration(result);
        configuration.performanceRatings = result.performanceRatings;
        configuration.hasMultipleCurrency = result.hasMultipleCurrency;
        configuration.currencies = result.currencies;
        configuration.homeCountryCurrency = result.homeCountryCurrency;
        configuration.customFieldMetadata = result.customFieldMetadata;
        return configuration;
    }

    async getCurrentCycleConfiguration(masterAccountId: string): Promise<Nullable<ICycleConfiguration>> {
        const receive = () => this._getDataRequest<Nullable<ICycleConfigurationFromService>>(
            `/api/accounts/${masterAccountId}/team/cycles/current/configuration`);
        const result = await this.getData<ICycleConfigurationFromService>(receive, false);

        if (!result) {
            return null;
        }

        return this.parseCycleConfiguration(result);
    }

    async saveConfiguration(masterAccountId: string, configuration: IConfiguration): Promise<void> {
        const configurationToService = this.composeConfiguration(configuration);
        const receive = () =>
            this._postDataRequest<typeof configurationToService, void>(
                `/api/accounts/${masterAccountId}/team/configuration`, configurationToService);
        await this.getData<void>(receive);
    }

    async getEligibilityRules(masterAccountId: string, cycleId: number): Promise<Nullable<IEligibilityRule[]>> {
        const receive = () => this._getDataRequest<IEligibilityRule[]>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/eligibilityrules`);
        return await this.getData<IEligibilityRule[]>(receive, false);
    }

    async deleteEligibilityRule(
        masterAccountId: string,
        cycleId: number,
        eligibilityRuleId: number): Promise<Nullable<number>> {
        const receive = () =>
            this._deleteDataRequest<number>(
                // tslint:disable-next-line:max-line-length
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/eligibilityrules/${eligibilityRuleId}`);
        return await this.getData<number>(receive, false);
    }

    async saveHrReviewerUser(
        masterAccountId: string,
        userId: string,
        employeeLevels: IBaseEmployeeLevel[]
    ): Promise<void> {
        const data = {
            userId,
            levels: employeeLevels.map(employeeLevel => {
                return { level: employeeLevel.level, levelName: employeeLevel.levelName };
            })
        };

        const receive = () => this._postDataRequest<typeof data, void>(
            `/api/accounts/${masterAccountId}/team/configuration/hrreviewer`, data);
        await this.getData<void>(receive, false);
    }

    async removeHrReviewerUser(
        masterAccountId: string, level: string, levelName: string
    ): Promise<void> {
        const data = {
            levels: [
                {
                    level: level,
                    levelName: levelName
                }
            ]
        };

        const receive = () => this._deleteWithRequestBody<typeof data, void>(
            // tslint:disable-next-line:max-line-length
            `/api/accounts/${masterAccountId}/team/configuration/hrreviewer`, data);
        await this.getData<void>(receive, false);
    }

    async getCycleConfigurations(masterAccountId: string): Promise<ICycleConfiguration[]> {
        const receive = () => this._getDataRequest<ICycleConfigurationFromService[]>(
            `/api/accounts/${masterAccountId}/team/cycles/configuration`);
        const result = await this.getData<ICycleConfigurationFromService[]>(receive, false);

        if (!result) {
            return [] as ICycleConfiguration[];
        }
        return result.map(value => this.parseCycleConfiguration(value));
    }

    parseCycleConfigurations(configs: ICycleConfigurationFromService[]): ICycleConfiguration[] {
        return configs.map(value => this.parseCycleConfiguration(value));
    }

    async saveCycleConfiguration(
        masterAccountId: string,
        cycleId: number,
        cycleConfiguration: ICycleConfiguration
    ): Promise<Nullable<number>> {
        const cycleConfigurationToService = this.composeCycleConfiguration(cycleConfiguration);

        const receive = () => this._putDataRequest<typeof cycleConfigurationToService, number>(
            `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration`, cycleConfigurationToService);
        return await this.getData<number>(receive);
    }

    async createCycleConfiguration(
        masterAccountId: string, cycleConfiguration: ICycleConfiguration): Promise<Nullable<number>> {
        const cycleConfigurationToService = this.composeNewCycleConfiguration(cycleConfiguration);

        const receive = () =>
            this._postDataRequest<typeof cycleConfigurationToService, number>(
                `/api/accounts/${masterAccountId}/team/cycles/configuration`, cycleConfigurationToService);
        return await this.getData<number>(receive, false);
    }

    async getCycleConfiguration(masterAccountId: string, cycleId: number): Promise<Nullable<ICycleConfiguration>> {
        const receive = () =>
            this._getDataRequest<ICycleConfigurationFromService>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration`);
        const result = await this.getData<ICycleConfigurationFromService>(receive, false);

        if (!result) {
            return null;
        }

        return this.parseCycleConfiguration(result);
    }

    async deleteCycleConfiguration(masterAccountId: string, cycleId: number): Promise<void> {
        const receive = () =>
            this._deleteDataRequest<void>(`/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration`);
        await this.getData<void>(receive, false);
    }

    async updateCycleConfigurationStatus(
        masterAccountId: string,
        cycleId: number,
        status: string
    ): Promise<Nullable<number>> {
        const receive = () =>
            this._putDataRequest<null, number>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/status/${status}`, null);
        return await this.getData<number>(receive);
    }

    async saveWorkflowConfiguration(
        masterAccountId: string,
        cycleId: number,
        workflowConfiguration: IWorkflowConfiguration
    ): Promise<void> {
        const workflowConfigurationToService = this.composeWorkflowConfiguration(workflowConfiguration);
        const receive = () =>
            this._postDataRequest<typeof workflowConfigurationToService, void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/workflow`,
                workflowConfigurationToService);
        await this.getData<void>(receive, false);
    }

    async exportFullWorkflowToCsv(masterAccountId: string, cycleId: number): Promise<void> {
        const receive = () =>
            this._downloadDataRequest(`/api/accounts/${masterAccountId}/team/cycles/${cycleId}/workflow/csv`,
                this.defaultCsvFilename,
                'GET');
        await this.getData<void>(receive, false);
    }

    async getWorkflowDetails(masterAccountId: string, cycleId: number): Promise<Nullable<string[][]>> {
        const receive = () =>
            this._getDataRequest<string[][]>(`/api/accounts/${masterAccountId}/team/cycles/${cycleId}/workflow`);
        return await this.getData<string[][]>(receive, false);
    }

    async getHrApproverUser(masterAccountId: string, cycleId: number): Promise<Nullable<IHrReviewerUser>> {
        const receive = () =>
            this._getDataRequest<IHrReviewerUser>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrapprover`);
        return await this.getData<IHrReviewerUser>(receive, false);
    }

    async getHrAssistantUser(
        masterAccountId: string, cycleId: number, userId: string): Promise<Nullable<IHrReviewerUser>> {
        const receive = () =>
            this._getDataRequest<IHrReviewerUser>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrassistants/${userId}`);
        return await this.getData<IHrReviewerUser>(receive, false);
    }

    async getHrAssistantUsers(masterAccountId: string, cycleId: number): Promise<Nullable<IHrReviewerUser[]>> {
        const receive = () =>
            this._getDataRequest<IHrReviewerUser[]>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrassistants`);
        return await this.getData<IHrReviewerUser[]>(receive, false);
    }

    async getHrReviewerUsers(masterAccountId: string): Promise<Nullable<IHrReviewerUser[]>> {
        const receive = () =>
            this._getDataRequest<IHrReviewerUser[]>(
                `/api/accounts/${masterAccountId}/team/configuration/hrreviewers`);
        return await this.getData<IHrReviewerUser[]>(receive, false);
    }

    async saveHrApproverUser(masterAccountId: string, cycleId: number, userId: string): Promise<void> {
        const data = {
            userId
        };

        const receive = () =>
            this._postDataRequest<typeof data, void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrapprover`, data);
        await this.getData<void>(receive, false);
    }

    async saveHrAssistantUser(
        masterAccountId: string,
        cycleId: number,
        userId: string): Promise<void> {
        const data = {
            userId
        };

        const receive = () =>
            this._postDataRequest<typeof data, void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrassistant`, data);
        await this.getData<void>(receive, false);
    }

    async removeHrApproverUser(masterAccountId: string, cycleId: number): Promise<void> {
        const receive = () =>
            this._deleteDataRequest<void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrapprover`);
        await this.getData<void>(receive, false);
    }

    async removeHrAssistantUser(masterAccountId: string, cycleId: number, userId: string): Promise<void> {
        const receive = () =>
            this._deleteDataRequest<void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/hrassistant/${userId}`);
        await this.getData<void>(receive, false);
    }

    async saveBonusFieldRules(
        bonusFieldRules: IBonusFieldRule[], masterAccountId: string, cycleId: string): Promise<void> {
        const receive = () =>
            this._postDataRequest<IBonusFieldRule[], void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/bonusFieldRules`, bonusFieldRules);
        await this.getData<void>(receive, false);
    }

    async getBonusFieldRules(masterAccountId: string, cycleId: string): Promise<Nullable<IBonusFieldRule[]>> {
        const receive = () =>
            this._getDataRequest<IBonusFieldRule[]>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/bonusFieldRules`);
        return await this.getData<IBonusFieldRule[]>(receive, false);
    }

    async deleteBonusFieldRules(masterAccountId: string, cycleId: string): Promise<void> {
        const receive = () =>
            this._deleteDataRequest<void>(
                `/api/accounts/${masterAccountId}/team/cycles/${cycleId}/configuration/bonusFieldRules`);
        await this.getData<void>(receive, false);
    }

    parseConfiguration(configurationFromService: IValueObject): IConfiguration {
        return JSON.parse(configurationFromService.value) as IConfiguration;
    }

    parseCycleConfiguration(
        cycleConfigurationFromService: ICycleConfigurationFromService): ICycleConfiguration {
        const cycleConfiguration = JSON.parse(cycleConfigurationFromService.value) as ICycleConfiguration;
        cycleConfiguration.id = cycleConfigurationFromService.cycleId;
        cycleConfiguration.cycleId = cycleConfigurationFromService.cycleId;
        cycleConfiguration.startDate = cycleConfigurationFromService.startDate;
        cycleConfiguration.endDate = cycleConfigurationFromService.endDate;
        cycleConfiguration.payEffectiveDate = cycleConfigurationFromService.payEffectiveDate;
        cycleConfiguration.closedDate = cycleConfigurationFromService.closedDate;
        cycleConfiguration.status = cycleConfigurationFromService.status;
        cycleConfiguration.previewMode = cycleConfigurationFromService.previewMode;
        if (cycleConfigurationFromService.budgetFields) {
            cycleConfiguration.budgetFields = this.parseCycleBudgetFields(cycleConfigurationFromService.budgetFields);
        }

        if (cycleConfigurationFromService.budgets) {
            cycleConfigurationFromService.budgets.forEach(budget => budget.id = budget.budgetCycleId);
            cycleConfiguration.budgets = cycleConfigurationFromService.budgets;
        }

        cycleConfiguration.workflowConfiguration =
            JSON.parse(cycleConfigurationFromService.workflowConfigurationValue) as IWorkflowConfiguration;
        return cycleConfiguration;
    }

    private parseCycleBudgetFields(
        cycleBudgetFieldsFromService: ICycleBudgetFieldFromService[]
    ): IObservableArray<ICycleBudgetField> {
        return cycleBudgetFieldsFromService.map(cycleBudgetFieldFromService => {
            const cycleBudgetField = cycleBudgetFieldFromService as ICycleBudgetField;
            const cycleBudgetFieldValue = JSON.parse(cycleBudgetFieldFromService.value) as ICycleBudgetFieldValue;
            if (cycleBudgetFieldValue.stock) {
                cycleBudgetField.stockDisplayType = cycleBudgetFieldValue.stock.displayType;
                cycleBudgetField.stockValue = cycleBudgetFieldValue.stock.value;
            }

            return cycleBudgetField;
        }) as IObservableArray<ICycleBudgetField>;
    }

    private composeConfiguration(configuration: IConfiguration): IValueObject {
        return {
            value: JSON.stringify(configuration)
        };
    }

    private composeCycleConfiguration(cycleConfiguration: ICycleConfiguration): IComposedCycleConfiguration {
        return {
            cycleId: cycleConfiguration.cycleId,
            startDate: cycleConfiguration.startDate ?? '',
            endDate: cycleConfiguration.endDate,
            payEffectiveDate: cycleConfiguration.payEffectiveDate,
            value: this.getCycleConfigurationValue(cycleConfiguration),
            previewMode: cycleConfiguration.previewMode
        };
    }

    private getCycleConfigurationValue(cycleConfiguration: ICycleConfiguration): string {
        return JSON.stringify({
            teamPageViews: cycleConfiguration.teamPageViews,
            ratingConfiguration: cycleConfiguration.ratingConfiguration,
            internalPayRange: cycleConfiguration.internalPayRange,
            comment: cycleConfiguration.comment
        });
    }

    private composeNewCycleConfiguration(cycleConfiguration: ICycleConfiguration): IComposedCycleConfiguration {
        const rules = [
            {
                code: RuleCode.ImmediateManager
            },
            {
                code: RuleCode.FollowHierarchy
            },
            {
                code: RuleCode.HumanResourceApprover
            }
        ];
        const defaultWorkflowConfiguration = {
            rules,
            originalRules: rules
        } as IWorkflowConfiguration;

        return {
            cycleId: cycleConfiguration.cycleId,
            endDate: cycleConfiguration.endDate,
            payEffectiveDate: cycleConfiguration.payEffectiveDate,
            value: this.getCycleConfigurationValue(cycleConfiguration),
            workflowConfigurationValue: JSON.stringify(defaultWorkflowConfiguration),
            previewMode: cycleConfiguration.previewMode
        } as IComposedCycleConfiguration;
    }

    private composeWorkflowConfiguration(workflowConfiguration: IWorkflowConfiguration): IWorkflowConfigurationRequest {
        return {
            workflowConfigurationValue: JSON.stringify(workflowConfiguration)
        };
    }
}

export { ConfigurationDataManager };
export default ConfigurationDataManager;